import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';
import ConfirmModal from '../../components/modals/ConfirmModal';
import SuccessModal from '../../components/modals/SuccessModal';
import ErrorModal from '../../components/modals/ErrorModal';
import { 
  getWeekNumber, 
  getWeekDateRange, 
  getCurrentWeek
} from '../../utils/weekCalculations';

const HolidaysPage = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('all');
  
  // Get current week data using shared utility
  const currentWeekData = getCurrentWeek();

  // Date filter state - using From/To week range
  const [fromYear, setFromYear] = useState(currentWeekData.year);
  const [fromWeek, setFromWeek] = useState(currentWeekData.week);
  const [toYear, setToYear] = useState(currentWeekData.year);
  const [toWeek, setToWeek] = useState(currentWeekData.week);
  
  // Calendar state
  const [showFromCalendar, setShowFromCalendar] = useState(false);
  const [showToCalendar, setShowToCalendar] = useState(false);
  
  const [selectedTab, setSelectedTab] = useState('pending');
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedSection, setSelectedSection] = useState('pending');
  
  const [showModal, setShowModal] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [formData, setFormData] = useState({
    user_id: '',
    start_date: '',
    end_date: '',
    description: '',
    holiday_type: 'annual_leave',
    status: 'pending'
  });

  // Custom modal states
  const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });
  const [successModal, setSuccessModal] = useState({ show: false, message: '' });
  const [errorModal, setErrorModal] = useState({ show: false, message: '' });
  
  // Denial modal state
  const [showDenialModal, setShowDenialModal] = useState(false);
  const [denialReason, setDenialReason] = useState('');
  const [itemsToDeny, setItemsToDeny] = useState([]);

  // Format week display using shared utility
  function formatWeekDisplay(year, weekNum) {
    const { startStr, endStr } = getWeekDateRange(year, weekNum);
    return `${year} Week ${weekNum} (${startStr} - ${endStr})`;
  }

  // Handle date click in calendar - matching Expenses/Overtime page logic
  function handleDateClick(date, isFrom = true) {
    const year = date.getFullYear();
    const weekNum = getWeekNumber(date, year);
    
    if (isFrom) {
      setFromYear(year);
      setFromWeek(weekNum);
      setShowFromCalendar(false);
      
      const fromRange = getWeekDateRange(year, weekNum);
      const toRange = getWeekDateRange(toYear, toWeek);
      
      if (fromRange.start > toRange.start) {
        setToYear(year);
        setToWeek(weekNum);
      }
    } else {
      const toRange = getWeekDateRange(year, weekNum);
      const fromRange = getWeekDateRange(fromYear, fromWeek);
      
      if (toRange.start < fromRange.start) {
        setFromYear(year);
        setFromWeek(weekNum);
      }
      
      setToYear(year);
      setToWeek(weekNum);
      setShowToCalendar(false);
    }
  }

  // Generate calendar for a specific month - matching other pages
  function generateCalendar(currentDate) {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    
    const dayOfWeek = firstDay.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    startDate.setDate(startDate.getDate() - daysToSubtract);
    
    const weeks = [];
    let currentWeek = [];
    
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      currentWeek.push(date);
      
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }
    
    return { weeks, month, year };
  }

  // Go to current week
  const goToCurrentWeek = () => {
    const { year, week } = getCurrentWeek();
    setFromYear(year);
    setFromWeek(week);
    setToYear(year);
    setToWeek(week);
  };

  useEffect(() => {
    fetchData();
  }, [selectedUser, fromYear, fromWeek, toYear, toWeek, selectedTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Get date range for API call using shared utility
      const fromRange = getWeekDateRange(fromYear, fromWeek);
      const toRange = getWeekDateRange(toYear, toWeek);
      const startDate = fromRange.startISO;
      const endDate = toRange.endISO;
      
      // Fetch pending holidays (always show regardless of date range)
      const pendingParams = {
        status: 'pending'
      };
      
      if (selectedUser !== 'all') {
        pendingParams.user_id = selectedUser;
      }
      
      const pendingResponse = await axios.get(`${API}/admin/holidays`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: pendingParams
      });
      
      // Fetch approved and denied holidays within date range
      const dateFilteredParams = {
        start_date: startDate,
        end_date: endDate
      };
      
      if (selectedUser !== 'all') dateFilteredParams.user_id = selectedUser;
      
      const dateFilteredResponse = await axios.get(`${API}/admin/holidays`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: dateFilteredParams
      });
      
      // Fetch users
      const usersRes = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Combine results, filtering out pending from date-filtered results to avoid duplicates
      const pendingHolidays = pendingResponse.data || [];
      const dateFilteredHolidays = (dateFilteredResponse.data || []).filter(h => h.status !== 'pending');
      
      setHolidays([...pendingHolidays, ...dateFilteredHolidays]);
      setUsers(usersRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setHolidays([]);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter holidays by tab
  const filteredHolidays = holidays.filter(holiday => {
    if (selectedTab === 'pending') return holiday.status === 'pending';
    if (selectedTab === 'approved') return holiday.status === 'approved';
    if (selectedTab === 'denied') return holiday.status === 'rejected';
    return true;
  });

  // Counts for tab badges
  const pendingCount = holidays.filter(h => h.status === 'pending').length;
  const approvedCount = holidays.filter(h => h.status === 'approved').length;
  const deniedCount = holidays.filter(h => h.status === 'rejected').length;

  // Selection handlers
  const handleItemSelect = (holidayId, section) => {
    if (section !== selectedSection) {
      setSelectedSection(section);
      setSelectedItems([holidayId]);
    } else {
      setSelectedItems(prev => 
        prev.includes(holidayId)
          ? prev.filter(id => id !== holidayId)
          : [...prev, holidayId]
      );
    }
  };

  const handleSelectAll = (section) => {
    const sectionHolidays = holidays.filter(h => {
      if (section === 'pending') return h.status === 'pending';
      if (section === 'approved') return h.status === 'approved';
      if (section === 'denied') return h.status === 'rejected';
      return false;
    });

    const allSelected = sectionHolidays.every(h => selectedItems.includes(h.id));
    
    if (allSelected) {
      setSelectedItems([]);
    } else {
      setSelectedSection(section);
      setSelectedItems(sectionHolidays.map(h => h.id));
    }
  };

  const clearSelection = () => {
    setSelectedItems([]);
  };

  // Bulk actions
  const getAvailableBulkActions = () => {
    if (selectedSection === 'pending') return ['approve', 'deny'];
    if (selectedSection === 'approved') return ['deny'];
    if (selectedSection === 'denied') return ['approve'];
    return [];
  };

  const handleBulkAction = async (action) => {
    if (selectedItems.length === 0) return;

    if (action === 'deny') {
      setItemsToDeny(selectedItems);
      setShowDenialModal(true);
      return;
    }

    let confirmMessage = '';
    if (action === 'approve') confirmMessage = `Approve ${selectedItems.length} holiday(s)?`;

    setConfirmModal({
      show: true,
      message: confirmMessage,
      onConfirm: async () => {
        try {
          const statusMap = {
            'approve': 'approved'
          };

          await Promise.all(
            selectedItems.map(id =>
              axios.put(
                `${API}/admin/holidays/${id}/status`,
                { status: statusMap[action] },
                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
              )
            )
          );

          clearSelection();
          fetchData();
          
          let actionText = '';
          if (action === 'approve') actionText = 'approved';
          else actionText = action + 'ed';
          
          setSuccessModal({
            show: true,
            message: `Successfully ${actionText} ${selectedItems.length} holiday(s)!`
          });
        } catch (error) {
          console.error(`Failed to ${action} holidays:`, error);
          setErrorModal({
            show: true,
            message: `Failed to ${action} holidays. Please try again.`
          });
        }
      }
    });
  };
  
  const handleBulkDeny = async () => {
    if (!denialReason.trim()) {
      setErrorModal({
        show: true,
        message: 'Please provide a reason for denial.'
      });
      return;
    }

    try {
      await Promise.all(
        itemsToDeny.map(id =>
          axios.put(
            `${API}/admin/holidays/${id}/status`,
            { status: 'rejected', rejection_reason: denialReason },
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
          )
        )
      );

      clearSelection();
      fetchData();
      setShowDenialModal(false);
      setDenialReason('');
      setItemsToDeny([]);
      
      setSuccessModal({
        show: true,
        message: `Successfully denied ${itemsToDeny.length} holiday(s)!`
      });
    } catch (error) {
      console.error('Failed to deny holidays:', error);
      setErrorModal({
        show: true,
        message: 'Failed to deny holidays. Please try again.'
      });
    }
  };

  const handleStatusChange = async (holidayId, newStatus) => {
    if (newStatus === 'rejected') {
      setItemsToDeny([holidayId]);
      setShowDenialModal(true);
      return;
    }
    
    try {
      await axios.put(
        `${API}/admin/holidays/${holidayId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      fetchData();
      setSuccessModal({
        show: true,
        message: 'Holiday status updated successfully!'
      });
    } catch (error) {
      console.error('Failed to update holiday status:', error);
      setErrorModal({
        show: true,
        message: 'Failed to update holiday status. Please try again.'
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const { user_id, ...holidayData } = formData;
      
      await axios.put(
        `${API}/admin/holidays/${selectedHoliday.id}`,
        holidayData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setSuccessModal({
        show: true,
        message: 'Holiday updated successfully!'
      });
      
      setShowModal(false);
      fetchData();
      resetForm();
    } catch (error) {
      console.error('Failed to save holiday:', error);
      
      let errorMessage = 'Failed to save holiday. Please try again.';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      }
      
      setErrorModal({
        show: true,
        message: errorMessage
      });
    }
  };

  const resetForm = () => {
    setFormData({
      user_id: '',
      start_date: '',
      end_date: '',
      description: '',
      holiday_type: 'annual_leave',
      status: 'pending'
    });
    setSelectedHoliday(null);
  };

  const openEditModal = (holiday) => {
    setSelectedHoliday(holiday);
    setFormData({
      user_id: holiday.user_id,
      start_date: holiday.start_date,
      end_date: holiday.end_date,
      description: holiday.description || '',
      holiday_type: holiday.holiday_type || 'annual_leave',
      status: holiday.status || 'pending'
    });
    setShowModal(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-500/20 text-green-300 border border-green-500/30';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30';
      case 'rejected':
        return 'bg-red-500/20 text-red-300 border border-red-500/30';
      default:
        return 'bg-slate-600 text-gray-300';
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'annual_leave':
        return 'bg-blue-500/20 text-blue-300 border border-blue-500/30';
      case 'sick_leave':
        return 'bg-purple-500/20 text-purple-300 border border-purple-500/30';
      case 'personal':
        return 'bg-slate-500/20 text-slate-300 border border-slate-500/30';
      case 'unpaid':
        return 'bg-orange-500/20 text-orange-300 border border-orange-500/30';
      default:
        return 'bg-slate-600 text-gray-300';
    }
  };

  const calculateDays = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  // Calendar Component - Dark Theme matching Expenses/Overtime page
  const Calendar = ({ onDateClick, onClose, selectedYear, selectedWeek, isFromCalendar }) => {
    // Initialize display date based on the selected week (not current date)
    const initialDisplayDate = () => {
      const weekRange = getWeekDateRange(selectedYear, selectedWeek);
      return weekRange.start;
    };
    const [displayDate, setDisplayDate] = useState(initialDisplayDate);
    const { weeks, month, year } = generateCalendar(displayDate);
    const calendarRef = useRef(null);
    
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (calendarRef.current && !calendarRef.current.contains(event.target)) {
          onClose();
        }
      };
      
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    const navigateMonth = (direction) => {
      const newDate = new Date(displayDate);
      newDate.setMonth(newDate.getMonth() + direction);
      
      const today = new Date();
      const maxDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      
      if (direction > 0 && newDate > maxDate) {
        return;
      }
      
      setDisplayDate(newDate);
    };
    
    const isSelectedWeek = (date) => {
      const year = date.getFullYear();
      const weekNum = getWeekNumber(date, year);
      return year === selectedYear && weekNum === selectedWeek;
    };
    
    return (
      <div className="relative">
        <div className={`absolute z-[9999] mt-2 ${isFromCalendar ? 'left-0' : 'right-0'}`}>
          <div 
            ref={calendarRef}
            className="bg-slate-800 rounded-xl shadow-xl border border-slate-600 p-6 w-80"
          >
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => navigateMonth(-1)}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h3 className="text-lg font-semibold text-white">
                {monthNames[month]} {year}
              </h3>
              <button
                onClick={() => navigateMonth(1)}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Weekday Headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, index) => (
                <div key={index} className="text-center text-xs font-semibold text-gray-400 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="space-y-1">
              {weeks.map((week, weekIndex) => (
                <div key={weekIndex} className="grid grid-cols-7 gap-1">
                  {week.map((date, dateIndex) => {
                    const isCurrentMonth = date.getMonth() === month;
                    const isToday = date.toDateString() === new Date().toDateString();
                    const isSelected = isSelectedWeek(date);
                    const isFutureDate = date > new Date();
                    
                    return (
                      <button
                        key={dateIndex}
                        onClick={() => !isFutureDate && onDateClick(date)}
                        disabled={isFutureDate}
                        className={`
                          p-2 text-sm rounded-lg transition-all duration-200 hover:scale-105
                          ${isFutureDate 
                            ? 'text-gray-600 cursor-not-allowed opacity-50' 
                            : isCurrentMonth 
                              ? 'text-white hover:bg-slate-700' 
                              : 'text-gray-500'
                          }
                          ${isSelected ? 'bg-purple-600 text-white hover:bg-purple-700' : ''}
                          ${isToday && !isSelected ? 'ring-2 ring-purple-400' : ''}
                        `}
                      >
                        {date.getDate()}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-900 -m-8 p-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 rounded-xl p-6 text-white mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold mb-1">Holidays</h1>
        <p className="text-purple-200">Review and approve holiday requests</p>
      </div>

      {/* Filters Section - Dark Theme */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-4 mb-6 overflow-visible">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-white ml-3">Filters & Date Range</h2>
          </div>
          
          <button
            onClick={goToCurrentWeek}
            className="bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 text-sm flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
            </svg>
            Current Week
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* From Filter */}
          <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600 relative">
            <h4 className="text-xs font-bold text-gray-300 mb-2 flex items-center">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
              </svg>
              From Date
            </h4>
            <div className="relative">
              <button
                onClick={() => setShowFromCalendar(true)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-left flex items-center justify-between hover:bg-slate-700"
              >
                <span className="truncate">{formatWeekDisplay(fromYear, fromWeek)}</span>
                <svg className="w-4 h-4 text-gray-400 ml-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
                </svg>
              </button>
              
              {showFromCalendar && (
                <Calendar
                  onDateClick={(date) => handleDateClick(date, true)}
                  onClose={() => setShowFromCalendar(false)}
                  selectedYear={fromYear}
                  selectedWeek={fromWeek}
                  isFromCalendar={true}
                />
              )}
            </div>
          </div>

          {/* To Filter */}
          <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600 relative">
            <h4 className="text-xs font-bold text-gray-300 mb-2 flex items-center">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
              </svg>
              To Date
            </h4>
            <div className="relative">
              <button
                onClick={() => setShowToCalendar(true)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-left flex items-center justify-between hover:bg-slate-700"
              >
                <span className="truncate">{formatWeekDisplay(toYear, toWeek)}</span>
                <svg className="w-4 h-4 text-gray-400 ml-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
                </svg>
              </button>
              
              {showToCalendar && (
                <Calendar
                  onDateClick={(date) => handleDateClick(date, false)}
                  onClose={() => setShowToCalendar(false)}
                  selectedYear={toYear}
                  selectedWeek={toWeek}
                  isFromCalendar={false}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Actions Section - Dark Theme */}
      {selectedItems.length > 0 && (
        <div className="mb-6 bg-purple-500/10 rounded-xl p-4 border border-purple-500/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mr-3">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div className="text-lg font-semibold text-white">
                Bulk Actions ({selectedItems.length} items selected)
              </div>
              <div className="ml-2 text-sm text-purple-400 font-medium capitalize">
                from {selectedSection} holidays
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {getAvailableBulkActions().includes('approve') && (
                <button
                  onClick={() => handleBulkAction('approve')}
                  className="bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-lg transition-all duration-200 flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Approve
                </button>
              )}
              {getAvailableBulkActions().includes('deny') && (
                <button
                  onClick={() => handleBulkAction('deny')}
                  className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg transition-all duration-200 flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Deny
                </button>
              )}
              <button
                onClick={clearSelection}
                className="text-gray-300 hover:text-white px-3 py-2 rounded-lg border border-slate-600 hover:bg-slate-700 transition-colors"
              >
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs - Dark Theme */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <div className="border-b border-slate-700">
          <nav className="-mb-px flex space-x-8 px-6">
            {['pending', 'approved', 'denied'].map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  setSelectedTab(tab);
                  clearSelection();
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  selectedTab === tab
                    ? 'border-purple-500 text-purple-400'
                    : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-slate-600'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                  tab === 'pending' ? 'bg-yellow-500/20 text-yellow-300' :
                  tab === 'approved' ? 'bg-green-500/20 text-green-300' :
                  'bg-red-500/20 text-red-300'
                }`}>
                  {tab === 'pending' ? pendingCount : tab === 'approved' ? approvedCount : deniedCount}
                </span>
              </button>
            ))}
          </nav>
        </div>

        {/* Holidays Table - Dark Theme */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            <span className="ml-3 text-gray-400">Loading holidays...</span>
          </div>
        ) : filteredHolidays.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
              </svg>
            </div>
              <h3 className="text-lg font-medium text-white mb-2">No {selectedTab} holidays</h3>
              <p className="text-gray-400">No {selectedTab} holidays found for the selected criteria.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-slate-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider w-12">
                      <input
                        type="checkbox"
                        checked={filteredHolidays.length > 0 && filteredHolidays.every(h => selectedItems.includes(h.id))}
                        ref={input => {
                          if (input) {
                            const someSelected = selectedItems.length > 0 && !filteredHolidays.every(h => selectedItems.includes(h.id));
                            input.indeterminate = someSelected;
                          }
                        }}
                        onChange={() => handleSelectAll(selectedTab)}
                        className="w-4 h-4 text-purple-600 bg-slate-700 border-slate-500 rounded focus:ring-purple-500"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Employee</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Period</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Days</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Reason</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                    {selectedTab === 'denied' && (
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Rejection Reason</th>
                    )}
                    {selectedTab === 'pending' && (
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredHolidays.map((holiday, index) => (
                    <tr 
                      key={holiday.id} 
                      className={`hover:bg-slate-700/50 transition-colors ${selectedItems.includes(holiday.id) ? 'bg-purple-500/10' : ''} ${index !== filteredHolidays.length - 1 ? 'border-b border-slate-700/50' : ''}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(holiday.id)}
                          onChange={() => handleItemSelect(holiday.id, selectedTab)}
                          className="w-4 h-4 text-purple-600 bg-slate-700 border-slate-500 rounded focus:ring-purple-500"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                              <span className="text-sm font-medium text-white">
                                {(holiday.user_name || 'U').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-white">
                              {holiday.user_name || 'Unknown User'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">
                          {new Date(holiday.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - {new Date(holiday.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-purple-400">
                          {calculateDays(holiday.start_date, holiday.end_date)} day{calculateDays(holiday.start_date, holiday.end_date) !== 1 ? 's' : ''}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getTypeBadge(holiday.holiday_type)}`}>
                          {holiday.holiday_type ? holiday.holiday_type.replace('_', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : 'Annual Leave'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-300 max-w-xs truncate" title={holiday.description}>
                          {holiday.description || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(holiday.status)}`}>
                          {holiday.status === 'rejected' ? 'Denied' : holiday.status.charAt(0).toUpperCase() + holiday.status.slice(1)}
                        </span>
                      </td>
                      {selectedTab === 'denied' && (
                        <td className="px-6 py-4">
                          <div className="text-xs text-red-400 max-w-xs truncate" title={holiday.rejection_reason}>
                            {holiday.rejection_reason || 'No reason provided'}
                          </div>
                        </td>
                      )}
                      {selectedTab === 'pending' && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          <button
                            onClick={() => handleStatusChange(holiday.id, 'approved')}
                            className="text-green-400 hover:text-green-300 px-3 py-1 rounded bg-green-500/10 hover:bg-green-500/20 border border-green-500/30"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleStatusChange(holiday.id, 'rejected')}
                            className="text-red-400 hover:text-red-300 px-3 py-1 rounded bg-red-500/10 hover:bg-red-500/20 border border-red-500/30"
                          >
                            Deny
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {/* Edit Modal - Dark Theme */}
      {showModal && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowModal(false)}
        >
          <div 
            className="bg-slate-900 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden border border-slate-700"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 px-8 py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">Edit Holiday</h3>
                    <p className="text-purple-200">Update holiday details</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowModal(false)}
                  className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-8 overflow-y-auto max-h-[calc(90vh-140px)]">
              <form onSubmit={handleSubmit} className="space-y-8">
                
                {/* Employee Selection Section */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    Employee Information
                  </h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Employee <span className="text-red-400">*</span>
                    </label>
                    <select
                      value={formData.user_id}
                      onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                      required
                      className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                    >
                      <option value="">Select Employee</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.full_name || `${user.first_name} ${user.last_name}`}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-400">Select the employee requesting holiday</p>
                  </div>
                </div>

                {/* Holiday Dates Section */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
                      </svg>
                    </div>
                    Holiday Period
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Start Date <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                        required
                        className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                      />
                      <p className="mt-1 text-xs text-gray-400">First day of holiday</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        End Date <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="date"
                        value={formData.end_date}
                        onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                        required
                        className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                      />
                      <p className="mt-1 text-xs text-gray-400">Last day of holiday</p>
                    </div>
                  </div>
                </div>

                {/* Holiday Details Section */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h4 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <div className="w-8 h-8 bg-orange-600 rounded-lg flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    Holiday Details
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Type <span className="text-red-400">*</span>
                      </label>
                      <select
                        value={formData.holiday_type}
                        onChange={(e) => setFormData({ ...formData, holiday_type: e.target.value })}
                        required
                        className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                      >
                        <option value="annual_leave">Annual Leave</option>
                        <option value="sick_leave">Sick Leave</option>
                        <option value="personal">Personal</option>
                        <option value="unpaid">Unpaid Leave</option>
                      </select>
                      <p className="mt-1 text-xs text-gray-400">Category of holiday request</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Status <span className="text-red-400">*</span>
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        required
                        className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                      >
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                      <p className="mt-1 text-xs text-gray-400">Approval status</p>
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Description <span className="text-gray-500">(Optional)</span>
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors resize-none"
                      placeholder="Reason for holiday request or additional notes..."
                    />
                    <p className="mt-1 text-xs text-gray-400">Additional information about the holiday request</p>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-4 pt-6 border-t border-slate-700">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-6 py-3 border border-slate-600 text-gray-300 rounded-lg hover:bg-slate-700 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Denial Modal - Dark Theme */}
      {showDenialModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md mx-4 border border-slate-700">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h3 className="text-xl font-bold text-white">Deny Holiday Request</h3>
              <button
                onClick={() => {
                  setShowDenialModal(false);
                  setDenialReason('');
                  setItemsToDeny([]);
                }}
                className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="mb-4">
                <p className="text-gray-300 mb-2">
                  Deny <span className="font-semibold text-red-400">{itemsToDeny.length} selected holiday request{itemsToDeny.length !== 1 ? 's' : ''}</span>
                </p>
                <p className="text-sm text-gray-400">Please provide a reason for the denial:</p>
              </div>

              <div className="space-y-3">
                <textarea
                  value={denialReason}
                  onChange={(e) => setDenialReason(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                  placeholder="e.g., Insufficient staff coverage during requested period..."
                  autoFocus
                />
              </div>

              {/* Modal Actions */}
              <div className="flex items-center justify-end space-x-4 pt-6 border-t border-slate-700 mt-6">
                <button
                  onClick={() => {
                    setShowDenialModal(false);
                    setDenialReason('');
                    setItemsToDeny([]);
                  }}
                  className="px-6 py-2 text-gray-300 border border-slate-600 rounded-lg hover:bg-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkDeny}
                  disabled={!denialReason.trim()}
                  className={`px-6 py-2 rounded-lg transition-all duration-200 ${
                    denialReason.trim()
                      ? 'bg-red-600 text-white hover:bg-red-500'
                      : 'bg-slate-700 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Deny Holiday Request{itemsToDeny.length > 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Modals */}
      <ConfirmModal
        isOpen={confirmModal.show}
        message={confirmModal.message}
        onConfirm={() => {
          if (confirmModal.onConfirm) {
            confirmModal.onConfirm();
          }
          setConfirmModal({ show: false, message: '', onConfirm: null });
        }}
        onClose={() => setConfirmModal({ show: false, message: '', onConfirm: null })}
      />

      <SuccessModal
        isOpen={successModal.show}
        message={successModal.message}
        onClose={() => setSuccessModal({ show: false, message: '' })}
      />

      <ErrorModal
        isOpen={errorModal.show}
        message={errorModal.message}
        onClose={() => setErrorModal({ show: false, message: '' })}
      />
    </div>
  );
};

export default HolidaysPage;
